/**
 * Collapse a nested translation tree into the flat, dot-delimited map that
 * formatjs looks keys up in.
 *
 * ember-intl flattened this internally until v9, which now hands `messages`
 * straight to formatjs — a nested object silently resolves nothing, so every
 * lookup misses. Our translations are authored as nested YAML and merged while
 * still nested, so they have to be flattened at the point they enter the intl
 * service.
 *
 * Semantics match ember-intl's removed `flattenKeys`: any non-null object is
 * recursed into (arrays included, yielding numeric segments), and `undefined`
 * values are dropped rather than emitted as keys.
 *
 * @param translations Nested translation tree, e.g. `{ a: { b: 'hi' } }`.
 * @returns Flat map keyed by dot-joined path, e.g. `{ 'a.b': 'hi' }`.
 */
export function flattenTranslations(translations: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};

  for (const key in translations) {
    if (!Object.prototype.hasOwnProperty.call(translations, key)) {
      continue;
    }

    const value = translations[key];

    if (value && typeof value === 'object') {
      const nested = flattenTranslations(value as Record<string, unknown>);

      for (const suffix in nested) {
        const translation = nested[suffix];
        if (typeof translation !== 'undefined') {
          result[`${key}.${suffix}`] = translation;
        }
      }
    } else if (typeof value !== 'undefined') {
      result[key] = value as string;
    }
  }

  return result;
}
